import { memo, useState } from 'react';
import { Box, styled, Typography } from '@mui/material';
import { Handle, type Node, type NodeProps, Position } from '@xyflow/react';
import { ExpandButton } from './expand-button';
import { OrBlockViewer } from './or-block-viewer';
import type { EnrollNodeData } from '../../../mappers/map-canvas-content.types';

const COLLAPSED_MAX_HEIGHT = 250;

export const EnrollNode = memo(({ data, selected }: NodeProps<Node<EnrollNodeData, 'enroll'>>) => {
  const rawOrBlocks = data.orBlocks ?? [];
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <NodeWrapper
      expanded={isExpanded}
      selected={selected}
      className="nopan"
      data-testid="enroll-node"
    >
      <HeaderWrapper>
        <TitleWrapper>
          <Typography variant="caption" fontWeight={700}>
            Enroll
          </Typography>
        </TitleWrapper>
      </HeaderWrapper>
      <Description>
        {rawOrBlocks.length === 0
          ? 'Configure this node to start enrolling shifts that fit the requirements. '
          : 'Enrolling shifts that fit the following requirements:'}
      </Description>

      <OrBlockViewer orBlocks={rawOrBlocks} isExpanded={isExpanded} />

      <ExpandButton
        expanded={isExpanded}
        onClick={() => setIsExpanded((prev) => !prev)}
        display={rawOrBlocks.length > 1}
      />

      <Handle type="source" position={Position.Right} isConnectable={false} />
    </NodeWrapper>
  );
});

const NodeWrapper = styled(Box)<{ expanded: boolean; selected?: boolean }>(
  ({ theme, expanded, selected }) => ({
    position: 'relative',
    backgroundColor: 'white',
    border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.lwSecondaryAccentLight[20]}`,
    borderRadius: theme.spacing(3),
    padding: theme.spacing(2),
    maxWidth: 320,
    transition: 'max-height 200ms ease',
    maxHeight: expanded ? 'none' : COLLAPSED_MAX_HEIGHT,
    overflow: expanded ? 'visible' : 'hidden',
    boxShadow: selected ? `0 0 0 2px ${theme.palette.primary.main}` : 'none',
  })
);

const HeaderWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 8,
});
const TitleWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
});

const Description = styled(Box)(({ theme }) => ({
  fontSize: '10px',
  textAlign: 'justify',
  paddingBottom: theme.spacing(2),
}));
